/**
 * FAQPage + BlogPosting structured data for blog posts rendered by the
 * Bloggle app.
 *
 * Bloggle renders its own article layout client-side (its content lives in
 * `.bloggle_rte`, loaded after its own script runs) instead of the theme's
 * native article template, so it has no <article> tag and no schema.org
 * markup of its own. Posts that DO use the theme's native article template
 * already get their schema from sections/main-article.liquid — this script
 * no-ops on those pages since `.bloggle_rte` won't exist.
 *
 * Convention this looks for inside the post body:
 *   - An H2 heading containing "FAQ" or "Frequently Asked Questions" marks
 *     the start of the FAQ section.
 *   - Each H3/H4 after that is a question; the paragraph/list text up to
 *     the next H3/H4 (or the next H2, or the end of the post) is its answer.
 *   - Only headings that actually end in a question mark are kept, so
 *     non-question headings after the FAQ block (e.g. a closing brand
 *     blurb) don't get mistaken for a question.
 * Requires 2+ valid question/answer pairs, in line with FAQPage best
 * practice (don't mark up pages with 0-1 FAQs).
 */
(function () {
  'use strict';

  var FAQ_HEADING_PATTERN = /\bfaq\b|frequently asked questions/i;
  var QUESTION_MARK_PATTERN = /[?？]\s*$/;
  var CONTENT_SELECTOR = '.bloggle_rte';
  var WAIT_TIMEOUT_MS = 15000;

  function stripAndCollapse(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  function extractFaqItems(root) {
    var items = [];
    var inFaq = false;
    var current = null;
    var nodes = root.querySelectorAll('h2, h3, h4, p, li');

    Array.prototype.forEach.call(nodes, function (node) {
      var tag = node.tagName;

      if (tag === 'H2') {
        if (FAQ_HEADING_PATTERN.test(node.textContent)) {
          inFaq = true;
          current = null;
        } else if (inFaq) {
          // A new, unrelated H2 section — the FAQ block has ended.
          inFaq = false;
          current = null;
        }
        return;
      }

      if (!inFaq) return;

      if (tag === 'H3' || tag === 'H4') {
        var question = stripAndCollapse(node.textContent);
        if (question) {
          current = { question: question, answerParts: [] };
          items.push(current);
        } else {
          current = null;
        }
        return;
      }

      if (current) {
        var text = stripAndCollapse(node.textContent);
        if (text) current.answerParts.push(text);
      }
    });

    return items
      .map(function (item) {
        return { question: item.question, answer: item.answerParts.join(' ') };
      })
      .filter(function (item) {
        return item.question && item.answer && QUESTION_MARK_PATTERN.test(item.question);
      });
  }

  function buildFaqSchema(items) {
    if (items.length < 2) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map(function (item) {
        return {
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
          }
        };
      })
    };
  }

  function buildArticleSchema(meta, root) {
    if (!meta || !meta.title) return null;

    var data = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: meta.title
    };
    if (meta.url) {
      data.url = meta.url;
      data.mainEntityOfPage = meta.url;
    }
    if (meta.image) data.image = [meta.image];
    if (meta.authorName) data.author = { '@type': 'Person', name: meta.authorName };
    if (meta.datePublished) data.datePublished = meta.datePublished;

    var firstParagraph = root.querySelector('p');
    if (firstParagraph) {
      var description = stripAndCollapse(firstParagraph.textContent).slice(0, 300);
      if (description) data.description = description;
    }

    return data;
  }

  function injectJsonLd(data) {
    var script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  }

  function run(root) {
    var faqSchema = buildFaqSchema(extractFaqItems(root));
    if (faqSchema) injectJsonLd(faqSchema);

    var articleSchema = buildArticleSchema(window.SwiftMotoArticleMeta, root);
    if (articleSchema) injectJsonLd(articleSchema);
  }

  function init() {
    var root = document.querySelector(CONTENT_SELECTOR);
    if (!root) return; // Not a Bloggle-rendered post.

    if (root.children.length > 0) {
      run(root);
      return;
    }

    // Bloggle populates this element asynchronously after its own script
    // loads, so wait for that instead of assuming it's already there.
    var settled = false;
    var observer = new MutationObserver(function () {
      if (settled || root.children.length === 0) return;
      settled = true;
      observer.disconnect();
      run(root);
    });
    observer.observe(root, { childList: true });

    // Safety net: stop watching if Bloggle never populates the content, so
    // this doesn't sit there forever.
    setTimeout(function () {
      if (!settled) observer.disconnect();
    }, WAIT_TIMEOUT_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
